import React from 'react';
import { FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { theme } from '../../../../../../../common/styles/theme';
import { Text } from '../../../../../../../common/components/Text/Text';
import { TBookVoices } from '../../../../../../../common/rest/endlessBook/endlessBookRest';
import { PlayIcon } from '../../../../../../../../icons/PlayIcon';
import { StopIcon } from '../../../../../../../../icons/StopIcon';
import { IBookVoiceSettingsProps } from './types';

const AUTO = '';

// "ru-RU-Chirp3-HD-Kore" → "Kore", as endless-book's own settings page shows it.
const voiceLabel = (name: string) => name
	.replace(/^[a-z]{2,3}-[A-Z]{2}-/, '')
	.replace(/^(Chirp3?-HD|Wavenet|Neural2|Standard)-/i, '');

// endless-book's narration voice for the book's language — saved per language, not per book.
export const BookVoiceSettings: React.FC<IBookVoiceSettingsProps> = ({
	languageName,
	onLoadVoices,
	onGetSample,
	onSetVoice,
}) => {
	// undefined = loading, null = failed
	const [data, setData] = React.useState<TBookVoices | null | undefined>(undefined);
	const [sampling, setSampling] = React.useState<string | null>(null);
	const [saving, setSaving] = React.useState(false);
	const audioRef = React.useRef<HTMLAudioElement | null>(null);
	// bumped on every play/stop, so a sample that arrives after another click is dropped
	const requestRef = React.useRef(0);

	const stopSample = () => {
		requestRef.current++;
		audioRef.current?.pause();
		audioRef.current = null;
		setSampling(null);
	};

	React.useEffect(() => {
		let alive = true;
		onLoadVoices().then((d) => { if (alive) setData(d); });
		return () => {
			alive = false;
			// also drops a sample still loading, or it would start playing after the modal closed
			requestRef.current++;
			audioRef.current?.pause();
		};
	}, []);

	const code = data?.languages.find(l => l.name === languageName)?.code;
	const voices = (code && data.voices[code]) || [];
	const current = (code && data.prefs[code]) || AUTO;

	const onPlay = async (voiceName: string) => {
		const wasPlaying = sampling === voiceName;
		stopSample();
		if (wasPlaying) return;
		setSampling(voiceName);
		const request = requestRef.current;
		const uri = await onGetSample(languageName, voiceName);
		if (request !== requestRef.current) return;
		if (!uri) return setSampling(null);
		const audio = new Audio(uri);
		audio.onended = () => setSampling((cur) => (cur === voiceName ? null : cur));
		audioRef.current = audio;
		audio.play().catch(() => setSampling(null));
	};

	const onUse = async (voiceName: string) => {
		setSaving(true);
		const prefs = await onSetVoice(code!, voiceName);
		if (prefs) setData({ ...data!, prefs });
		setSaving(false);
	};

	const playButton = (voiceName: string) => (
		<Tooltip title={sampling === voiceName ? 'Stop' : 'Play sample'}>
			<IconButton
				size={'small'}
				// inside a menu item: play without choosing the voice
				onClick={(e) => { e.stopPropagation(); onPlay(voiceName); }}
				onMouseDown={(e) => e.stopPropagation()}
			>
				{sampling === voiceName ? <StopIcon sx={{ fontSize: 12 }} /> : <PlayIcon sx={{ fontSize: 12 }} />}
			</IconButton>
		</Tooltip>
	);

	const options = code
		? [{ name: AUTO, label: 'Automatic', gender: '' }, ...voices.map(v => ({ name: v.name, label: voiceLabel(v.name), gender: v.gender }))]
		: [];

	return (
		<Stack direction={'row'} alignItems={'center'} gap={.5} sx={{ flex: 1, minWidth: 0 }}>
			<FormControl sx={{ flex: 1, minWidth: 0 }}>
				<InputLabel shrink htmlFor={'book-voice-select'}>Narration voice</InputLabel>
				<Select
					id={'book-voice-select'}
					variant={'filled'}
					displayEmpty
					value={code ? current : ''}
					disabled={!code || saving}
					sx={{
						'.MuiSelect-select': {
							fontWeight: 400 + ' !important',
						},
					}}
					onChange={(e) => onUse(e.target.value as string)}
					renderValue={(value) => data === undefined
						? 'Loading…'
						: !code
							? (data === null ? 'Not available right now' : 'No voices for this language')
							: options.find(o => o.name === value)?.label}
				>
					{options.map(o => (
						<MenuItem key={o.name || 'auto'} value={o.name} sx={{ gap: 1 }}>
							{playButton(o.name)}
							<Text variant={'subtitle2'} sx={{ flex: 1 }}>{o.label}</Text>
							{o.gender && <Text sx={{ color: theme.palette.grey[500] }}>{o.gender.toLowerCase()}</Text>}
						</MenuItem>
					))}
				</Select>
				<FormHelperText sx={{ mx: 0 }}>For every {languageName} book you read</FormHelperText>
			</FormControl>
			{!!code && playButton(current)}
		</Stack>
	);
};
